const WHEEL_THRESHOLD = 15;
const SWIPE_COMMIT_PX = 40;
const DRAG_CLICK_THRESHOLD = 5;
const DRAG_CLICK_SUPPRESS_MS = 300;
const WHEEL_LOCK_MS = 300;
const SCROLL_DURATION_MS = 280;

const DEFAULT_EDGE_STATE = {
    middle: 'is-carousel-at-middle',
    end: 'is-carousel-at-end',
};

const getPeekOptions = (container) => ({
    edgeEl: container.getAttribute('data-carousel-peek-edge')
        ? container.closest(container.getAttribute('data-carousel-peek-edge'))
        : null,
    edgeState: {
        middle: container.getAttribute('data-carousel-peek-state-middle') || DEFAULT_EDGE_STATE.middle,
        end: container.getAttribute('data-carousel-peek-state-end') || DEFAULT_EDGE_STATE.end,
    },
});

// Nearest slide to the viewport centre — works in LTR and RTL
const getActiveIndex = (wrap, slides) => {
    const center = wrap.getBoundingClientRect().left + wrap.clientWidth / 2;

    return [...slides].reduce(
        (closest, slide, index) => {
            const rect = slide.getBoundingClientRect();
            const distance = Math.abs(center - (rect.left + rect.width / 2));

            return distance < closest.distance ? { index, distance } : closest;
        },
        { index: 0, distance: Infinity },
    ).index;
};

const getScrollDeltaForSlide = (wrap, slides, index) => {
    const slide = slides[index];
    if (!slide) return 0;

    const wrapRect = wrap.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();

    if (index === 0) {
        return slideRect.left - wrapRect.left;
    }

    if (index === slides.length - 1) {
        return slideRect.right - wrapRect.right;
    }

    return slideRect.left + slideRect.width / 2 - (wrapRect.left + wrapRect.width / 2);
};

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2);

const smoothScrollTo = (wrap, targetLeft, duration = SCROLL_DURATION_MS) => {
    const start = wrap.scrollLeft;
    const delta = targetLeft - start;

    if (Math.abs(delta) < 1) return;

    wrap.style.scrollSnapType = 'none';
    const startTime = performance.now();

    const tick = (now) => {
        const progress = Math.min(1, (now - startTime) / duration);
        wrap.scrollLeft = start + delta * easeInOut(progress);

        if (progress < 1) {
            requestAnimationFrame(tick);
            return;
        }

        wrap.style.removeProperty('scroll-snap-type');
    };

    requestAnimationFrame(tick);
};

// Horizontal-only scroll — avoids scrollIntoView moving the page vertically
const scrollToSlide = (wrap, slides, index, smooth = true) => {
    const target = wrap.scrollLeft + getScrollDeltaForSlide(wrap, slides, index);

    if (smooth) {
        smoothScrollTo(wrap, target);
        return;
    }

    wrap.scrollLeft = target;
};

const getDirectionFromDelta = (delta, isRtl) => (delta > 0 ? (isRtl ? 1 : -1) : isRtl ? -1 : 1);

const updateEdgeState = (edgeEl, edgeState, index, slideCount) => {
    if (!edgeEl) return;

    edgeEl.classList.toggle(edgeState.middle, index > 0 && index < slideCount - 1);
    edgeEl.classList.toggle(edgeState.end, index === slideCount - 1 && slideCount > 1);
};

export const initPeekScrollCarousel = (container, isActiveCarousel) => {
    const { edgeEl, edgeState } = getPeekOptions(container);
    const wrap = container.querySelector('.carousel__slides');
    const slides = wrap?.querySelectorAll('.carousel__slide');

    if (!wrap || !slides?.length) return null;

    const bullets = container.querySelectorAll('.carousel__bullet');
    const isRtl = document.body.classList.contains('rtl');

    let dragging = false;
    let dragStartIndex = 0;
    let startX = 0;
    let lastX = 0;
    let dragFinishedAt = 0;
    let wheelLocked = false;

    const syncEdgeState = (index) => {
        updateEdgeState(edgeEl, edgeState, index, slides.length);
    };

    const setActive = (index) => {
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
        bullets.forEach((bullet, i) => bullet.classList.toggle('is-active', i === index));
        syncEdgeState(index);
    };

    const goTo = (index, smooth = true) => {
        if (!isActiveCarousel(container)) return;

        index = Math.max(0, Math.min(index, slides.length - 1));
        setActive(index);
        scrollToSlide(wrap, slides, index, smooth);
    };

    const commitSwipe = (delta, startIndex) => {
        if (Math.abs(delta) < SWIPE_COMMIT_PX) {
            goTo(getActiveIndex(wrap, slides), true);
            return;
        }

        const nearest = getActiveIndex(wrap, slides);
        const direction = getDirectionFromDelta(delta, isRtl);
        const nextIndex = Math.max(0, Math.min(slides.length - 1, startIndex + direction));
        const target = Math.abs(nearest - startIndex) > 0 ? nearest : nextIndex;

        goTo(target, true);
    };

    wrap.addEventListener(
        'scroll',
        () => {
            // Update bullets as soon as a new slide is nearest the centre, not after scroll settles
            setActive(getActiveIndex(wrap, slides));
        },
        { passive: true },
    );

    if ('onscrollend' in wrap) {
        wrap.addEventListener(
            'scrollend',
            () => {
                setActive(getActiveIndex(wrap, slides));
            },
            { passive: true },
        );
    }

    bullets.forEach((bullet, index) => {
        bullet.addEventListener('click', (e) => {
            e.preventDefault();
            goTo(index);
        });
    });

    // Desktop mouse drag
    wrap.addEventListener('pointerdown', (e) => {
        if (!isActiveCarousel(container) || e.pointerType !== 'mouse' || e.button !== 0) return;

        dragging = true;
        dragStartIndex = getActiveIndex(wrap, slides);
        startX = lastX = e.clientX;
        wrap.setPointerCapture(e.pointerId);
        wrap.style.scrollSnapType = 'none';
        e.preventDefault();
    });

    wrap.addEventListener('pointermove', (e) => {
        if (!dragging) return;

        wrap.scrollBy({ left: lastX - e.clientX });
        lastX = e.clientX;
    });

    const finishDrag = () => {
        if (!dragging) return;

        dragging = false;
        wrap.style.removeProperty('scroll-snap-type');

        const delta = lastX - startX;

        if (Math.abs(delta) > DRAG_CLICK_THRESHOLD) {
            dragFinishedAt = Date.now();
        }

        commitSwipe(delta, dragStartIndex);
    };

    wrap.addEventListener('pointerup', (e) => {
        if (wrap.hasPointerCapture(e.pointerId)) wrap.releasePointerCapture(e.pointerId);
        finishDrag();
    });

    wrap.addEventListener('pointercancel', finishDrag);

    // Block link clicks briefly after a drag, not after a plain click
    wrap.addEventListener(
        'click',
        (e) => {
            if (Date.now() - dragFinishedAt > DRAG_CLICK_SUPPRESS_MS) return;

            e.preventDefault();
            e.stopPropagation();
        },
        true,
    );

    // Magic Mouse / trackpad — one slide per gesture
    container.addEventListener(
        'wheel',
        (e) => {
            if (!isActiveCarousel(container) || wheelLocked || Math.abs(e.deltaX) < WHEEL_THRESHOLD) return;

            e.preventDefault();

            goTo(getActiveIndex(wrap, slides) + getDirectionFromDelta(-e.deltaX, isRtl));

            wheelLocked = true;
            setTimeout(() => {
                wheelLocked = false;
            }, WHEEL_LOCK_MS);
        },
        { passive: false },
    );

    const refresh = () => {
        if (!isActiveCarousel(container)) {
            wrap.scrollLeft = 0;
            setActive(0);
            return;
        }

        goTo(getActiveIndex(wrap, slides), false);
    };

    refresh();
    return refresh;
};
